import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';

export function TermsOfServicePage() {
  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground/30">
      <Helmet>
        <title>Terms of Service | FIVE LENS Enneagram</title>
        <meta name="description" content="Terms of Service for the FIVE LENS Enneagram platform and services." />
      </Helmet>
      
      <div className="max-w-4xl mx-auto px-6 lg:px-8 mb-8">
        <Breadcrumb items={[{ label: 'Terms of Service' }]} />
      </div>

      <article className="max-w-4xl mx-auto px-6 lg:px-8 mb-20 bg-white border border-ground-deep shadow-sm p-8 md:p-12">
        <header className="mb-12 text-center border-b border-ground-deep/50 pb-8">
          <h1 className="text-4xl md:text-5xl font-serif text-ink mb-6">Terms of Service</h1>
          <p className="text-ink/70 text-lg font-light">
            Last Updated: October 2023
          </p>
        </header>

        <div className="prose prose-brand max-w-none text-ink/80 font-light leading-relaxed space-y-8">
          <section>
            <h2 className="text-2xl font-serif text-ink mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing and using the FIVE LENS Development Platform (5LDP) and our associated websites, products, and services (collectively, the "Services"), you agree to be bound by these Terms of Service. If you do not agree to these Terms, you may not access or use the Services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif text-ink mb-4">2. Description of Services</h2>
            <p>
              FIVE LENS Enneagram (Ennea International) provides psychometric assessments, personal feedback reports, coaching frameworks, and organizational development tools. Our Services are intended to support personal mastery and team effectiveness through evidence-based psychological insights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif text-ink mb-4">3. User Responsibilities</h2>
            <p>
              You agree to provide accurate, current, and complete information when creating an account or taking our assessments. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif text-ink mb-4">4. Intellectual Property Rights</h2>
            <p>
              All content, assessments, reports, frameworks, methodologies, trademarks, and other intellectual property included in the Services are the exclusive property of Ennea International or its licensors. You are granted a limited, non-exclusive, non-transferable license to access and use the Services for your personal or authorized professional use only.
            </p>
            <p className="mt-4">
              You may not copy, modify, distribute, sell, or lease any part of our Services or included software, nor may you reverse engineer or attempt to extract the source code of that software, unless laws prohibit those restrictions or you have our written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif text-ink mb-4">5. Privacy and Data Protection</h2>
            <p>
              Your privacy is critically important to us. Please review our <Link to="/privacy-policy" className="text-rule-3 hover:underline">Privacy Policy</Link> to understand how we collect, use, and protect your personal and psychometric data across different global regions (including GDPR, POPIA, and CCPA compliance).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif text-ink mb-4">6. Practitioner Certification</h2>
            <p>
              Use of our professional assessment tools for commercial coaching or organizational consulting requires active certification. Certified practitioners must adhere to our ethical guidelines, maintain their credentialing, and operate within the scope of their competence. Ennea International reserves the right to revoke certification for breach of these standards.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif text-ink mb-4">7. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Ennea International shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from (a) your access to or use of or inability to access or use the Services; (b) any conduct or content of any third party on the Services; or (c) unauthorized access, use, or alteration of your transmissions or content.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif text-ink mb-4">8. Governing Law</h2>
            <p>
              These Terms shall be governed and construed in accordance with the laws of South Africa, without regard to its conflict of law provisions. Any dispute arising from these Terms will be subject to the exclusive jurisdiction of the courts located in South Africa.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif text-ink mb-4">9. Changes to Terms</h2>
            <p>
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. By continuing to access or use our Services after those revisions become effective, you agree to be bound by the revised terms.
            </p>
          </section>
        </div>
      </article>
    </main>
  );
}
